import { ActionCreator } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { GlobalAccountingApi } from '../../../api/globalAccountingApi';
import { toastMsg } from '../../../components/Toast/toastMsg';
import { IPagination } from '../../../types/pagination';
import { IProjectInvoice } from '../../project/types/invoice';
import { AccountingState } from '../reducers';
import { IDirectCostInvoice } from './../../project/types/invoice';
import { IAccountingChartData, IPeriodType } from './../types/accounting';
import * as constants from './constants';


export type GlobalAccountingThunkResult<R> = ThunkAction<Promise<R>, AccountingState, null, GlobalAccountingActions>
export type GlobalAccountingThunkDispatchType = ThunkDispatch<AccountingState, null, GlobalAccountingActions>

export interface AccountingHasErrored {
  type: constants.ACCOUNTING_HAS_ERRORED,
  hasErrored: boolean
}

/** SPONSOR AND PTC */
export interface GetInvoiceYearsSuccess {
  type: constants.GET_INVOICE_YEARS_SUCCESS
  years: number[]
}

export interface SetPaginatedInvoicesePage {
  type: constants.SET_PAGINATED_INVOICES_ACTIVE_PAGE
  page: number
}

export interface GetPaginatedInvoicesSuccess {
  type: constants.GET_PAGINATED_INVOICES_SUCCESS
  data: IPagination<IProjectInvoice[]>
}

export interface CreateProjectInvoiceSuccess {
  type: constants.CREATE_INVOICE_SUCCESS
  invoice: IProjectInvoice
}

export interface UpdateProjectInvoiceSuccess {
  type: constants.UPDATE_INVOICE_SUCCESS
  invoice: IProjectInvoice
}

export interface DeleteProjectInvoiceSuccess {
  type: constants.DELETE_INVOICE_SUCCESS
  invoice: IProjectInvoice
}

export interface GetProjectInvoiceSuccess {
  type: constants.GET_INVOICE_SUCCESS
  invoice: IProjectInvoice
}

export interface SelectProjectInvoice {
  type: constants.SET_SELECTED_INVOICE
  invoice: IProjectInvoice
}

export interface GetAccountingChartDataSuccess {
  type: constants.GET_ACCOUNTING_CHART_DATA_SUCCESS
  data: IAccountingChartData
}

/** DC */
export interface SetPaginatedDCInvoicesePage {
  type: constants.SET_PAGINATED_DC_INVOICES_ACTIVE_PAGE
  page: number
}

export interface GetPaginatedDCInvoicesSuccess {
  type: constants.GET_PAGINATED_DC_INVOICES_SUCCESS
  data: IPagination<IDirectCostInvoice[]>
}

export interface CreateProjectDCInvoiceSuccess {
  type: constants.CREATE_DC_INVOICE_SUCCESS
  invoice: IDirectCostInvoice
}

export interface UpdateProjectDCInvoiceSuccess {
  type: constants.UPDATE_DC_INVOICE_SUCCESS
  invoice: IDirectCostInvoice
}

export interface DeleteProjectDCInvoiceSuccess {
  type: constants.DELETE_DC_INVOICE_SUCCESS
  invoice: IDirectCostInvoice
}

export interface GetProjectDCInvoiceSuccess {
  type: constants.GET_DC_INVOICE_SUCCESS
  invoice: IDirectCostInvoice
}

export interface SelectProjectDCInvoice {
  type: constants.SET_SELECTED_DC_INVOICE
  invoice: IDirectCostInvoice
}

export interface GetAccountingReceiptsChartDataSuccess {
  type: constants.GET_ACCOUNTING_RECEIPTS_CHART_DATA_SUCCESS
  data: IAccountingChartData
}

/** ALL */
export interface SetPeriodType {
  type: constants.SET_PERIOD_TYPE
  period: IPeriodType
}

export interface SetSelectedYear {
  type: constants.SET_SELECTED_YEAR
  year: number
}

export interface SelectTab {
  type: constants.SELECT_TAB,
  tab: string
}


export const accountingHasErrored = (hasErrored: boolean, error: Error, action?: string): AccountingHasErrored => {
  if (action) {
    const errorMsg = error.message
    toastMsg(errorMsg, action, 'error')
  }
  return {
    type: constants.ACCOUNTING_HAS_ERRORED,
    hasErrored: hasErrored
  }
}

/** SPONSOR AND PTC */
export const getInvoiceYears: ActionCreator<GlobalAccountingThunkResult<number[]>>
  = (queryParams?: Record<string, string>): GlobalAccountingThunkResult<number[]> => {
    return async (dispatch: GlobalAccountingThunkDispatchType) => {
      return GlobalAccountingApi.getInvoiceYears(queryParams)
        .then(response => {
          dispatch(getInvoiceYearsSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.GET_INVOICE_YEARS))
          return [] as number[]
        })
    }
  }

export const getInvoiceYearsSuccess = (years: number[]): GetInvoiceYearsSuccess => {
  return {
    type: constants.GET_INVOICE_YEARS_SUCCESS,
    years
  }
}

export const getPaginatedInvoices: ActionCreator<GlobalAccountingThunkResult<IPagination<IProjectInvoice[]>>>
  = (queryParams: Record<string, string>): GlobalAccountingThunkResult< IPagination<IProjectInvoice[]>> => {
    return async (dispatch: GlobalAccountingThunkDispatchType) => {
      return GlobalAccountingApi.getPaginatedInvoices(queryParams)
        .then(response => {
          dispatch(getPaginatedInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.GET_PAGINATED_INVOICES))
          return {} as  IPagination<IProjectInvoice[]>
        })
    }
  }

export const getPaginatedInvoiceSuccess = (data: IPagination<IProjectInvoice[]>): GetPaginatedInvoicesSuccess => {
  return {
    type: constants.GET_PAGINATED_INVOICES_SUCCESS,
    data
  }
}

export const setPaginatedInvoicesActivePage = (page: number): SetPaginatedInvoicesePage => {
  return {
    type: constants.SET_PAGINATED_INVOICES_ACTIVE_PAGE,
    page: page
  }
}

export const createProjectInvoice: ActionCreator<GlobalAccountingThunkResult<IProjectInvoice>>
  = (projectId: string | number, invoice: IProjectInvoice): GlobalAccountingThunkResult<IProjectInvoice> => {
    return async (dispatch: GlobalAccountingThunkDispatchType) => {
      return GlobalAccountingApi.createInvoice(projectId, invoice)
        .then(response => {
          dispatch(createProjectInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.CREATE_INVOICE))
          return {} as IProjectInvoice
        })
    }
  }

export const createProjectInvoiceSuccess = (invoice: IProjectInvoice): CreateProjectInvoiceSuccess => {
  toastMsg(`Create ${invoice.number} successfully.`, constants.CREATE_INVOICE_SUCCESS, 'success')
  return {
    type: constants.CREATE_INVOICE_SUCCESS,
    invoice
  }
}

export const updateProjectInvoice: ActionCreator<GlobalAccountingThunkResult<IProjectInvoice>>
  = (projectId: string | number, invoice: IProjectInvoice): GlobalAccountingThunkResult<IProjectInvoice> => {
    return async (dispatch: GlobalAccountingThunkDispatchType) => {
      return GlobalAccountingApi.updateInvoice(projectId, invoice)
        .then(response => {
          dispatch(updateProjectInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.UPDATE_INVOICE))
          return {} as IProjectInvoice
        })
    }
  }

export const updateProjectInvoiceSuccess = (invoice: IProjectInvoice): UpdateProjectInvoiceSuccess => {
  toastMsg(`Updated ${invoice.number} successfully.`, constants.UPDATE_INVOICE_SUCCESS, 'success')
  return {
    type: constants.UPDATE_INVOICE_SUCCESS,
    invoice
  }
}

export const deleteProjectInvoice: ActionCreator<GlobalAccountingThunkResult<string | number | void>>
  = (projectId: string | number, invoice: IProjectInvoice): GlobalAccountingThunkResult<string | number | void> => {
    return async (dispatch: GlobalAccountingThunkDispatchType) => {
      return GlobalAccountingApi.deleteInvoice(projectId, invoice)
        .then(response => {
          dispatch(deleteProjectInvoiceSuccess(invoice))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.DELETE_INVOICE))
        })
    }
  }

export const deleteProjectInvoiceSuccess = (invoice: IProjectInvoice): DeleteProjectInvoiceSuccess => {
  toastMsg(`Deleted ${invoice.number} successfully.`, constants.DELETE_INVOICE_SUCCESS, 'success')
  return {
    type: constants.DELETE_INVOICE_SUCCESS,
    invoice
  }
}

export const getProjectInvoice: ActionCreator<GlobalAccountingThunkResult<IProjectInvoice>>
  = (projectId: string | number, invoice_id: string | number): GlobalAccountingThunkResult<IProjectInvoice> => {
    return async (dispatch: GlobalAccountingThunkDispatchType) => {
      return GlobalAccountingApi.getInvoice(projectId, invoice_id)
        .then(response => {
          dispatch(getProjectInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.GET_INVOICE))
          return {} as IProjectInvoice
        })
    }
  }

export const getProjectInvoiceSuccess = (invoice: IProjectInvoice): GetProjectInvoiceSuccess => {
  return {
    type: constants.GET_INVOICE_SUCCESS,
    invoice
  }
}

export const setSelectedInvoice = (invoice: IProjectInvoice): SelectProjectInvoice => {
  return {
    type: constants.SET_SELECTED_INVOICE,
    invoice
  }
}

export const getAccountingChartData: ActionCreator<GlobalAccountingThunkResult<IAccountingChartData>>
  = (queryParams: Record<string, string>): GlobalAccountingThunkResult<IAccountingChartData> => {
    return async (dispatch: GlobalAccountingThunkDispatchType) => {
      return GlobalAccountingApi.getAccountingChartData(queryParams)
        .then(response => {
          dispatch(getAccountingChartDataSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.GET_ACCOUNTING_CHART_DATA))
          return {} as IAccountingChartData
        })
    }
  }

export const getAccountingChartDataSuccess = (data: IAccountingChartData): GetAccountingChartDataSuccess => {
  return {
    type: constants.GET_ACCOUNTING_CHART_DATA_SUCCESS,
    data
  }
}

/** DC */
export const getPaginatedDCInvoices: ActionCreator<GlobalAccountingThunkResult<IPagination<IDirectCostInvoice[]>>>
  = (queryParams: Record<string, string>): GlobalAccountingThunkResult< IPagination<IDirectCostInvoice[]>> => {
    return async (dispatch: GlobalAccountingThunkDispatchType) => {
      return GlobalAccountingApi.getPaginatedDCInvoices(queryParams)
        .then(response => {
          dispatch(getPaginatedDCInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.GET_PAGINATED_DC_INVOICES))
          return {} as  IPagination<IDirectCostInvoice[]>
        })
    }
  }

export const getPaginatedDCInvoiceSuccess = (data: IPagination<IDirectCostInvoice[]>): GetPaginatedDCInvoicesSuccess => {
  return {
    type: constants.GET_PAGINATED_DC_INVOICES_SUCCESS,
    data
  }
}

export const setPaginatedDCInvoicesActivePage = (page: number): SetPaginatedDCInvoicesePage => {
  return {
    type: constants.SET_PAGINATED_DC_INVOICES_ACTIVE_PAGE,
    page: page
  }
}

export const createProjectDCInvoice: ActionCreator<GlobalAccountingThunkResult<IDirectCostInvoice>>
  = (projectId: string | number, invoice: IDirectCostInvoice): GlobalAccountingThunkResult<IDirectCostInvoice> => {
    return async (dispatch: GlobalAccountingThunkDispatchType) => {
      return GlobalAccountingApi.createDCInvoice(projectId, invoice)
        .then(response => {
          dispatch(createProjectDCInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.CREATE_DC_INVOICE))
          return {} as IDirectCostInvoice
        })
    }
  }

export const createProjectDCInvoiceSuccess = (invoice: IDirectCostInvoice): CreateProjectDCInvoiceSuccess => {
  toastMsg(`Create ${invoice.number} successfully.`, constants.CREATE_DC_INVOICE_SUCCESS, 'success')
  return {
    type: constants.CREATE_DC_INVOICE_SUCCESS,
    invoice
  }
}

export const updateProjectDCInvoice: ActionCreator<GlobalAccountingThunkResult<IDirectCostInvoice>>
  = (projectId: string | number, invoice: IDirectCostInvoice): GlobalAccountingThunkResult<IDirectCostInvoice> => {
    return async (dispatch: GlobalAccountingThunkDispatchType) => {
      return GlobalAccountingApi.updateDCInvoice(projectId, invoice)
        .then(response => {
          dispatch(updateProjectDCInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.UPDATE_DC_INVOICE))
          return {} as IDirectCostInvoice
        })
    }
  }

export const updateProjectDCInvoiceSuccess = (invoice: IDirectCostInvoice): UpdateProjectDCInvoiceSuccess => {
  toastMsg(`Updated ${invoice.number} successfully.`, constants.UPDATE_INVOICE_SUCCESS, 'success')
  return {
    type: constants.UPDATE_DC_INVOICE_SUCCESS,
    invoice
  }
}

export const deleteProjectDCInvoice: ActionCreator<GlobalAccountingThunkResult<string | number | void>>
  = (projectId: string | number, invoice: IDirectCostInvoice): GlobalAccountingThunkResult<string | number | void> => {
    return async (dispatch: GlobalAccountingThunkDispatchType) => {
      return GlobalAccountingApi.deleteDCInvoice(projectId, invoice)
        .then(response => {
          dispatch(deleteProjectDCInvoiceSuccess(invoice))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.DELETE_INVOICE))
        })
    }
  }

export const deleteProjectDCInvoiceSuccess = (invoice: IDirectCostInvoice): DeleteProjectDCInvoiceSuccess => {
  toastMsg(`Deleted ${invoice.number} successfully.`, constants.DELETE_INVOICE_SUCCESS, 'success')
  return {
    type: constants.DELETE_DC_INVOICE_SUCCESS,
    invoice
  }
}

export const getProjectDCInvoice: ActionCreator<GlobalAccountingThunkResult<IDirectCostInvoice>>
  = (projectId: string | number, invoice_id: string | number): GlobalAccountingThunkResult<IDirectCostInvoice> => {
    return async (dispatch: GlobalAccountingThunkDispatchType) => {
      return GlobalAccountingApi.getDCInvoice(projectId, invoice_id)
        .then(response => {
          dispatch(getProjectDCInvoiceSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.GET_DC_INVOICE))
          return {} as IDirectCostInvoice
        })
    }
  }

export const getProjectDCInvoiceSuccess = (invoice: IDirectCostInvoice): GetProjectDCInvoiceSuccess => {
  return {
    type: constants.GET_DC_INVOICE_SUCCESS,
    invoice
  }
}

export const setSelectedDCInvoice = (invoice: IDirectCostInvoice): SelectProjectDCInvoice => {
  return {
    type: constants.SET_SELECTED_DC_INVOICE,
    invoice
  }
}

export const getAccountingReceiptsChartData: ActionCreator<GlobalAccountingThunkResult<IAccountingChartData>>
  = (queryParams: Record<string, string>): GlobalAccountingThunkResult<IAccountingChartData> => {
    return async (dispatch: GlobalAccountingThunkDispatchType) => {
      return GlobalAccountingApi.getAccountingReceiptsChartData(queryParams)
        .then(response => {
          dispatch(getAccountingReceiptsChartDataSuccess(response))
          return response
        })
        .catch(error => {
          dispatch(accountingHasErrored(true, error.response.data, constants.GET_ACCOUNTING_RECEIPTS_CHART_DATA))
          return {} as IAccountingChartData
        })
    }
  }

export const getAccountingReceiptsChartDataSuccess = (data: IAccountingChartData): GetAccountingReceiptsChartDataSuccess => {
  return {
    type: constants.GET_ACCOUNTING_RECEIPTS_CHART_DATA_SUCCESS,
    data
  }
}

/** ALL */
export const setPeriodType = (period: IPeriodType): SetPeriodType => {
  return {
    type: constants.SET_PERIOD_TYPE,
    period
  }
}

export const setSelectedYear = (year: number): SetSelectedYear => {
  return {
    type: constants.SET_SELECTED_YEAR,
    year
  }
}

export const selectTab = (tab: string): SelectTab => {
  return {
    type: constants.SELECT_TAB,
    tab
  }
}

export type GlobalAccountingActions
  = AccountingHasErrored
  | SetPaginatedInvoicesePage
  | GetPaginatedInvoicesSuccess
  | CreateProjectInvoiceSuccess
  | UpdateProjectInvoiceSuccess
  | DeleteProjectInvoiceSuccess
  | GetProjectInvoiceSuccess
  | SelectProjectInvoice
  | GetAccountingChartDataSuccess
  | SetPaginatedDCInvoicesePage
  | GetPaginatedDCInvoicesSuccess
  | CreateProjectDCInvoiceSuccess
  | UpdateProjectDCInvoiceSuccess
  | DeleteProjectDCInvoiceSuccess
  | GetProjectDCInvoiceSuccess
  | SelectProjectDCInvoice
  | GetAccountingReceiptsChartDataSuccess
  | SetPeriodType
  | SetSelectedYear
  | SelectTab
  | GetInvoiceYearsSuccess
